import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import PurchaseOrderFormModal from '../components/PurchaseOrderFormModal';
import { generatePoPdf } from '../components/poPdf';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'partial', label: 'Partially Received' },
  { id: 'received', label: 'Received' },
];

const STATUS_BADGE = {
  pending:  { bg: '#fff3cd', color: '#856404', label: 'Pending' },
  partial:  { bg: '#cce5ff', color: '#004085', label: 'Partially Received' },
  received: { bg: '#d4edda', color: '#155724', label: 'Received' },
};

const PurchaseOrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);

  const limit = 20;

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await chemicalService.getPurchaseOrders(params);
      setOrders(res.data || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      alert('❌ Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusFilter = (id) => { setStatusFilter(id); setPage(1); };
  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  const handleSaved = () => { setShowFormModal(false); fetchOrders(); };

  const handleDownloadPdf = async (po) => {
    try {
      // Fetch full PO with items + vendor details (list rows don't include items)
      const res = await chemicalService.getPurchaseOrderById(po.id);
      generatePoPdf(res.data);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to generate PDF'));
    }
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete purchase order ${po.po_no}? This cannot be undone.`)) return;
    try {
      await chemicalService.deletePurchaseOrder(po.id);
      alert('✅ Purchase order deleted');
      fetchOrders();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };

  const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

  const badge = (status) => {
    const b = STATUS_BADGE[status] || STATUS_BADGE.pending;
    return <span style={{ background: b.bg, color: b.color, padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>{b.label}</span>;
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => handleStatusFilter(f.id)}
              style={{ ...styles.filterTab, ...(statusFilter === f.id ? styles.filterTabActive : {}) }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowFormModal(true)} style={styles.addButton}>+ New Purchase Order</button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder="Search PO no. or vendor..."
          style={styles.searchInput}
        />
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>PO #</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Vendor</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={styles.emptyCell}>Loading...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={6} style={styles.emptyCell}>No purchase orders yet</td></tr>
            ) : (
              orders.map((po) => (
                <tr key={po.id} style={styles.row}>
                  <td style={styles.td}><strong>{po.po_no}</strong></td>
                  <td style={styles.td}>{fmtDate(po.po_date)}</td>
                  <td style={styles.td}>{po.vendor_name}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(po.grand_total)}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{badge(po.status)}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button onClick={() => handleDownloadPdf(po)} style={styles.pdfButton} title="Download PDF">📄</button>
                      {po.status === 'pending' && (
                        <button onClick={() => handleDelete(po)} style={styles.deleteButton} title="Delete">🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={styles.pageButton}>‹ Prev</button>
          <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={styles.pageButton}>Next ›</button>
        </div>
      )}

      {showFormModal && (
        <PurchaseOrderFormModal
          onClose={() => setShowFormModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

const styles = {
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterTab: { padding: '8px 16px', background: '#f4f4f4', color: '#666', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  filterTabActive: { background: '#8e44ad', color: 'white' },
  addButton: { padding: '10px 20px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 },
  searchInput: { width: '100%', maxWidth: '360px', padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  tableWrap: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#faf7fc' },
  th: { padding: '14px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 14px', fontSize: '13px', color: '#2c3e50' },
  emptyCell: { padding: '40px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  pdfButton: { padding: '6px 10px', background: '#eafaf1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  deleteButton: { padding: '6px 10px', background: '#fdecea', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '18px' },
  pageButton: { padding: '8px 16px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  pageInfo: { fontSize: '13px', color: '#7f8c8d', fontWeight: 600 },
};

export default PurchaseOrdersTab;